import React, { useEffect, useRef, useState } from 'react';
import { match, P } from 'ts-pattern';
import type {
  DataElementReference,
  DataObject,
  ITextElement,
  Primitive,
  Token,
} from '@basis-theory/react-elements';
import { TextElement } from '@basis-theory/react-elements';

import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toggle } from '@fragment/ui/src/components/Toggle/Toggle';

import type { OnboardingState } from './onboardingReducer';
import type { PersonOnboardingState } from './personOnboardingReducer';

const isDataElementReference = (
  value: unknown
): value is DataElementReference =>
  typeof value === 'object' &&
  value !== null &&
  'correlationId' in value &&
  'elementId' in value &&
  'path' in value;

const stateKeyIsString = (
  value: string | string[] | boolean | undefined
): value is string =>
  match(value)
    .with(P.string, () => true)
    .otherwise(() => false);

const stateKeyIsBoolean = (
  value: string | string[] | boolean | undefined
): value is boolean =>
  match(value)
    .with(P.boolean, () => true)
    .otherwise(() => false);

const stateKeyIsStringArray = (
  value: string | string[] | boolean | undefined
): value is string[] =>
  match(value)
    .with(P.array(P.string), () => true)
    .otherwise(() => false);

const fontColor = (isDarkMode: boolean) =>
  isDarkMode ? 'rgb(255 255 255)' : 'rgb(13 13 13)';

const bgColor = (isDarkMode: boolean) =>
  isDarkMode ? 'rgb(38 38 38)' : 'rgb(230 230 230)';

const hoverBgColor = (isDarkMode: boolean) =>
  isDarkMode ? 'rgb(13 13 13)' : 'rgb(255 255 255)';

type BasisTheoryTextElementProps = {
  id: string;
  label: string;
  isDarkMode?: boolean;
  update: (id: string, ref: React.RefObject<ITextElement>) => void;
  token: Token | null;
};

export const BasisTheoryTextElement = ({
  id,
  label,
  isDarkMode = false,
  update,
  token,
}: BasisTheoryTextElementProps) => {
  const ref = useRef<ITextElement>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isReady || !token || !ref.current) {
      return;
    }

    if (!token.data) {
      return;
    }

    const data = token.data as DataObject<Primitive>;
    const dataElement = data[id];
    if (isDataElementReference(dataElement)) {
      ref.current.setValue(dataElement);
    }
  }, [token, id, isReady]);

  return (
    <div className="flex flex-col w-full">
      <span className="text-md text-main-500 ">{label}</span>
      <TextElement
        id={id}
        ref={ref}
        onReady={() => {
          setIsReady(true);
        }}
        onBlur={() => update(id, ref)}
        style={{
          base: {
            fontFamily: 'FragmentMono, monospace',
            fontSize: '20px',
            lineHeight: '27px',
            color: fontColor(isDarkMode),
            backgroundColor: bgColor(isDarkMode),
            ':hover': {
              backgroundColor: hoverBgColor(isDarkMode),
            },
            padding: '0',
          },
          empty: {
            color: fontColor(isDarkMode),
            backgroundColor: bgColor(isDarkMode),
            ':hover': {
              backgroundColor: hoverBgColor(isDarkMode),
            },
          },
          complete: {
            color: fontColor(isDarkMode),
            backgroundColor: bgColor(isDarkMode),
            ':hover': {
              backgroundColor: hoverBgColor(isDarkMode),
            },
          },
        }}
      />
    </div>
  );
};

type OnboardingTextInputProps = {
  /** Key in onboarding reducer state, e.g. "businessName" */
  stateKey: string;
  label: string;
  state: OnboardingState | PersonOnboardingState;
  setField: (stateKey: string, value: string) => void;
};

export const OnboardingTextInput = ({
  stateKey,
  label,
  state,
  setField,
}: OnboardingTextInputProps) => {
  const value = state[stateKey];
  if (stateKeyIsString(value)) {
    return (
      <div className="flex flex-col w-full">
        <span className="text-md text-main-500 ">{label}</span>
        <TextInput
          padding="p-f0"
          height="h-f2"
          size="md"
          width="w-full"
          value={value}
          onChange={(e) => setField(stateKey, e.target.value)}
        />
      </div>
    );
  }
  return <span>Reduce State Not A String Field!</span>;
};

type OnboardingTextAreaProps = {
  stateKey: string;
  label: string;
  state: OnboardingState;
  setField: (stateKey: string, value: string) => void;
};

export const OnboardingTextArea = ({
  stateKey,
  label,
  state,
  setField,
}: OnboardingTextAreaProps) => {
  const value = state[stateKey];
  if (stateKeyIsString(value)) {
    return (
      <div className="flex flex-col">
        <span className="text-md text-main-500">{label}</span>
        <textarea
          value={value}
          onChange={(e) => setField(stateKey, e.target.value)}
          className="text-main text-md w-full min-h-f2 h-fit max-h-f12 p-f0 border-[0.5px] border-main-300 appearance-none select-none focus:outline-none focus:ring-0 focus:border-main-300"
        />
      </div>
    );
  }
  return <span>Reducer State Not a String!</span>;
};

type OnboardingDropdownProps = {
  stateKey: string;
  label: string;
  options: { value: string; label: string }[];
  state: OnboardingState;
  setField: (stateKey: string, value: string) => void;
};

export const OnboardingDropdown = ({
  stateKey,
  label,
  options,
  state,
  setField,
}: OnboardingDropdownProps) => {
  const value = state[stateKey];
  if (stateKeyIsString(value)) {
    return (
      <div className="flex flex-col ">
        <span className="text-md text-main-500">{label}</span>
        <Dropdown
          displayValue={value}
          options={options}
          onChange={(newValue) => setField(stateKey, newValue)}
          size="md"
          width="w-full"
          height="h-f2"
          padding="p-f0"
          backgroundColor="bg-main-200"
        />
      </div>
    );
  }
  return <span>Reducer State Not a String!</span>;
};

type OnboardingToggleProps = {
  stateKey: string;
  label: string;
  state: OnboardingState;
  setField: (stateKey: string, value: boolean) => void;
};

export const OnboardingToggle = ({
  stateKey,
  label,
  state,
  setField,
}: OnboardingToggleProps) => {
  const value = state[stateKey];
  if (stateKeyIsBoolean(value)) {
    return (
      <div className="flex flex-col">
        <span className="text-md text-main min-w-f21">{label}</span>
        <Toggle
          controlled
          activeButton={value ? 'left' : 'right'}
          leftLabel="Yes"
          rightLabel="No"
          onLeftClick={() => setField(stateKey, true)}
          onRightClick={() => setField(stateKey, false)}
        />
      </div>
    );
  }
  return <span>{`Reducer state ${stateKey} is not a boolean`}</span>;
};

type OnboardingCheckBoxProps = {
  checked: boolean;
  label: React.ReactNode | string;
  onClick: () => void;
};

export const OnboardingCheckBox = ({
  checked,
  label,
  onClick,
}: OnboardingCheckBoxProps) => (
  <div className="flex flex-row items-start gap-x-[1ch]">
    <button
      type="button"
      onClick={onClick}
      className="aspect-square h-f2 text-main bg-transparent hover:bg-negative border-[0.5px] border-main-300"
    >
      {checked ? '✓' : ''}
    </button>
    {typeof label === 'string' ? (
      <span className="text-md text-main">{label}</span>
    ) : (
      label
    )}
  </div>
);

type OnboardingChecklistProps = {
  stateKey: string;
  label: string;
  items: { value: string; label: string }[];
  state: OnboardingState;
  setField: (stateKey: string, value: string[]) => void;
  allowOther: boolean;
};

export const OnboardingChecklist = ({
  stateKey,
  label,
  items,
  state,
  setField,
  allowOther,
}: OnboardingChecklistProps) => {
  const value = state[stateKey];
  const [otherValue, setOtherValue] = useState<string>('');
  const [useOther, setUseOther] = useState<boolean>(false);

  if (stateKeyIsStringArray(value)) {
    return (
      <div className="flex flex-col ">
        <span className="text-md text-main-500 pb-f1">{label}</span>
        <div className="flex flex-col gap-y-f1 p-f1 border-main-300 border-[0.5px] max-h-f15 overflow-y-scroll">
          {items.map((item) => (
            <OnboardingCheckBox
              key={item.value}
              checked={value.includes(item.value)}
              label={item.label}
              onClick={() =>
                setField(
                  stateKey,
                  value.includes(item.value)
                    ? value.filter((v) => v !== item.value)
                    : [...value, item.value]
                )
              }
            />
          ))}
          {allowOther && (
            <div className="flex w-full gap-x-[1ch]">
              <button
                type="button"
                onClick={() => {
                  setUseOther((prev) => !prev);
                }}
                className="aspect-square h-f2 text-main bg-transparent hover:bg-negative border-[0.5px] border-main-300"
              >
                {useOther ? '✓' : ''}
              </button>
              {useOther ? (
                <TextInput
                  padding="p-f0"
                  height="h-f2"
                  size="md"
                  width="w-full"
                  placeholder="Other"
                  value={otherValue}
                  onChange={(e) => {
                    setOtherValue(e.target.value);
                  }}
                />
              ) : (
                <span>Other</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
  return <span>{`Reducer state ${stateKey} is not a string array`}</span>;
};
